// =====================================================
// NOTIFICATION TYPES (using mail_list table)
// =====================================================

import { NotificationTemplateMapper, type Translate } from '@/services/notifications/NotificationTemplateMapper';

export type NotificationType = 'info' | 'warning' | 'error' | 'success';

export type NotificationActionType = 'redirect' | 'execute_function' | 'open_modal';

export interface NotificationActionPayload {
  path?: string;
  params?: Record<string, string>;
  functionName?: string;
  modalId?: string;
  [key: string]: any; // Permette payload personalizzati futuri
}

export interface NotificationAction {
  type: NotificationActionType;
  payload: NotificationActionPayload;
}

// Interface per la notifica dal database (mail_list con notifiche)
export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  message: string;
  read: boolean;
  action?: NotificationAction;
  redirect_link?: string;
  created_at: string;
  updated_at: string;
}

// Tipo per il payload del realtime
export interface NotificationRealtimePayload {
  new: any; // mail_list row
  old?: any; // mail_list row
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
}

function buildRedirectAction(redirectLink?: string | null): NotificationAction | undefined {
  if (!redirectLink) {
    return undefined;
  }

  try {
    const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const url = new URL(redirectLink, baseOrigin);

    return {
      type: 'redirect',
      payload: {
        path: `${url.pathname}${url.search}${url.hash}`,
      },
    };
  } catch {
    return {
      type: 'redirect',
      payload: {
        path: redirectLink,
      },
    };
  }
}

/**
 * Helper per mappare da user_notifications a Notification.
 *
 * Type, title, message e action sono dedotti dal template via
 * NotificationTemplateMapper e tradotti nella lingua attiva: `mail.message` è
 * renderizzato dal trigger DB al momento dell'INSERT ed è quindi congelato nella
 * lingua di allora, per cui viene usato solo come fallback per i template che il
 * frontend ancora non conosce.
 *
 * `t` va passato dal chiamante: il risultato dipende dalla lingua e va ricalcolato
 * quando questa cambia.
 */
export function mapMailToNotification(mail: any, t: Translate): Notification {
  const mapping = NotificationTemplateMapper.mapTemplate(mail.template, mail.payload, t);
  const message = NotificationTemplateMapper.isKnownTemplate(mail.template)
    ? mapping.message
    : mail.message || mapping.message;
  const redirectAction = buildRedirectAction(mail.redirect_link);

  return {
    id: mail.id,
    user_id: mail.user_id,
    type: mapping.type,
    title: mapping.title,
    message,
    read: mail.read || false,
    action: redirectAction || mapping.action,
    redirect_link: mail.redirect_link || undefined,
    created_at: mail.created_at,
    updated_at: mail.updated_at,
  };
}
